import React, { useState, useEffect, useRef, ReactNode, useMemo } from 'react'
import './InternalChatSidePanel.css'
import Avatar from '@components/common/Avatar/Avatar.component'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { Dropdown } from 'react-bootstrap'
import StartChatModal from '../Modals/StartChatModal/StartChatModal.component'
import EditFavoritesModal from '../Modals/EditFavoritesModal/EditFavoritesModal.component'
import InternalChatMembersModal from './InternalChatMembersModal.component'
import InternalChatChangeNameModal from './InternalChatChangeNameModal.component'
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import InsertLinkModal from '@components/common/InsertLinkModal/InsertLinkModal.component'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  ChatChannel,
  ChatDirection,
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { getFullName } from '@utils/fullname.util'
import { IFavoritedUser } from '@dataTypes/user-favorite.type'
import {
  addInternalChatMessage,
  bulkUpdateChatParticipantsAction,
  createInternalConversation,
  deleteChatParticipant,
  fetchAllInternalChats,
  markInternalConversationAsRead,
  updateInternalChat,
  bulkUpdateConversation,
  fetchMessagingSettingsByClinic,
} from '@store/actions'
import { getFirstLineFromHTML } from '@utils/messageFormatter.util'
import { getInternalChatUnreadCount } from '@utils/chat.util'
import { sanitizeRichTextHtml } from '@utils/sanitize-html.util'
import moment from 'moment'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { setSelectedChatId } from '@store/reducers/chat-internal.reducer'
import SPTextEditor from './SidePanelTextEditor/SPTextEditor.component'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import { IEditorInput } from '@components/common/TextEditor/useTextEditor'

type InternalChatSidePanelProps = {
  isHidden: boolean
  toggle: () => void
}

const INTERNAL_CHAT_LOADING_CONTAINER = '.internal-chat-sidepanel'

const InternalChatSidePanel: React.FC<InternalChatSidePanelProps> = ({
  isHidden,
  toggle,
}): React.ReactElement => {
  const dispatch = useAppDispatch()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const messagingSettings = useAppSelector(
    (state) => state.messagingSettings.current,
  )
  const trackMessageStatus = useMemo(
    () => messagingSettings?.track_message_status ?? true,
    [messagingSettings],
  )
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [searchValue, setSearchValue] = useState('')
  const [messageValue, setMessageValue] = useState('')
  const [selectedChat, setSelectedChat] = useState<IChatConversation | null>(
    null,
  )
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([])
  const [showInsertLinkModal, setShowInsertLinkModal] = useState(false)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [showStartChatModal, setShowStartChatModal] = useState(false)
  const [showMemberMenu, setShowMemberMenu] = useState(false)
  const [showMembersList, setShowMembersList] = useState(false)
  const [showChangeNameModal, setShowChangeNameModal] = useState(false)
  const [showEditFavoritesModal, setShowEditFavoritesModal] = useState(false)
  const [showConfirmRemoveMemberModal, setShowConfirmRemoveMemberModal] =
    useState(false)
  const [selectedRecipient, setSelectedRecipient] =
    useState<IChatParticipant | null>(null)

  const [showArchivedList, setShowArchivedList] = useState(false)
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [showConfirmLeaveGroupModal, setShowConfirmLeaveGroupModal] =
    useState(false)
  const [editorInput, setEditorInput] = useState<IEditorInput>({
    text: '',
    htmlContent: '',
  })

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!selectedClinic?.clinic_id) return
    void dispatch(fetchMessagingSettingsByClinic())
  }, [dispatch, selectedClinic?.clinic_id])
  const inputRef = useRef<HTMLInputElement>(null)
  const toolbarRef = useRef<RichTextEditorComponent>(null)

  const {
    inbox,
    archived: archivedConversations,
    data: conversations,
    favoriteUsers,
    users,
    selectedChatId,
  } = useAppSelector((state) => state.chatInternal)

  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').user_id

  const getChatDirection = (chat: IChatMessage): string => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (chat.sender_id === user?.user_id) {
      return 'Outgoing'
    } else {
      return 'Incoming'
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversations])

  const getSenderDetails = (
    sender_id: string,
    participants: IChatParticipant[],
    identifySelf?: boolean,
  ): IChatParticipant | undefined => {
    const participant = participants.find((p) => p.user_id === sender_id)
    if (identifySelf) {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (participant?.user_id === user.user_id) {
        return {
          ...participant,
          username: 'You',
        } as IChatParticipant
      }
    }
    return participant
  }

  const getConvoPhoto = (
    participants: IChatParticipant[],
    isGroup?: boolean,
  ): JSX.Element => {
    const defaultPhoto = (
      <Avatar
        key="no-photo"
        source="https://cdn-icons-png.flaticon.com/128/9131/9131478.png"
        alt="No photo"
      />
    )

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (participants.length === 1 || participants.length === 2) {
      const participant = participants.filter(
        (p) => p.user_id !== user.user_id,
      )[0]
      return (
        <Avatar
          source={
            participant?.photo ??
            'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
          }
          alt={participant?.username}
        />
      )
    } else if (participants.length > 2 || isGroup) {
      const _groupMembers = participants.filter(
        (p) => p.user_id !== user.user_id,
      )

      if (_groupMembers.length) {
        return (
          <div className="multiple-participant-image" key="multiphoto">
            <Avatar
              source={
                _groupMembers[0]?.photo ??
                'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
              }
              alt={_groupMembers[0]?.username}
            />
            <Avatar
              source={
                _groupMembers[1]?.photo ??
                'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
              }
              alt={_groupMembers[1]?.username}
            />
          </div>
        )
      }
    }
    return defaultPhoto
  }

  const highlightText = (text: string, highlight: string): ReactNode => {
    if (text.length && highlight.length) {
      const regExp = new RegExp(`(${highlight})`, 'gi')
      const parts = text.split(regExp)
      return parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={index} className="search-highlight">
            {part}
          </span>
        ) : (
          part
        ),
      )
    } else {
      return <span>{text}</span>
    }
  }

  const getParticipantDisplayName = (
    p: IChatParticipant | undefined,
  ): string => {
    if (!p) return 'Unknown user'
    if (p.username === 'You') return 'You'
    const u = p.user
    const nameData =
      u && (u.first_name != null || u.last_name != null)
        ? {
            first_name: u.first_name ?? '',
            last_name: u.last_name ?? '',
            middle_name: u.provider_profile?.middle_name ?? u.middle_name,
          }
        : null
    const full = getFullName(nameData, false, false, '')
    return full || p.username || 'Unknown user'
  }

  const getConversationName = (conversation: IChatConversation): string => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (conversation.name) {
      return conversation.name
    }
    const participants = conversation.chat_participant?.filter(
      (p) => p.user_id !== user.user_id,
    )
    return participants?.map((p) => getParticipantDisplayName(p)).join(', ')
  }

  const getDisplayChat = (chats: IChatMessage[]): string => {
    const length = chats?.length
    if (length && length > 0) {
      return getFirstLineFromHTML(chats[length - 1].content)
    }
    return ''
  }

  const generateTimeDisplay = (timestampString: Date | null): string[] => {
    if (timestampString) {
      const timestamp = new Date(timestampString)
      const dateNow = new Date()
      const longDisplay = moment(timestamp).format('MM/DD/YY - h:mm A')

      if (dateNow.getFullYear() === timestamp.getFullYear()) {
        const dateDifference = dateNow.getDate() - timestamp.getDate()
        if (timestamp.getMonth() == dateNow.getMonth() && dateDifference <= 6) {
          if (
            moment(dateNow).format('MM-DD-YY') ===
            moment(timestamp).format('MM-DD-YY')
          ) {
            const minuteDifference = Math.floor(
              (dateNow.getTime() - timestamp.getTime()) / 60000,
            )
            if (minuteDifference < 1) {
              return ['Just now', longDisplay]
            } else if (minuteDifference === 1)
              return [`1 minute ago`, longDisplay]
            else if (minuteDifference < 60)
              return [`${minuteDifference} minutes ago`, longDisplay]
            else if (Math.floor(minuteDifference / 60) === 1)
              return [`1 hour ago`, longDisplay]
            else
              return [
                `${Math.floor(minuteDifference / 60)} hours ago`,
                longDisplay,
              ]
          } else return [moment(timestamp).format('ddd'), longDisplay]
        } else return [moment(timestamp).format('MMM DD'), longDisplay]
      } else return [moment(timestamp).format('MMM DD, YYYY'), longDisplay]
    } else return ['']
  }

  const chats = showArchivedList ? archivedConversations : inbox

  const filteredChats = chats.filter((chat) => {
    const searchLower = searchValue.toLowerCase().trim()
    const participantMatch = chat.chat_participant.some((participant) =>
      participant.username?.toLowerCase().includes(searchLower),
    )
    const nameMatch = chat.name?.toLowerCase().includes(searchLower)
    const messageMatch = chat.chat_message.some((message) =>
      message.content?.toLowerCase().includes(searchLower),
    )

    return (
      participantMatch || nameMatch || messageMatch || searchLower.length === 0
    )
  })

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const isAllSelected = selectedChatIds.length === filteredChats.length
      const isSomeSelected = selectedChatIds.length > 0 && !isAllSelected
      selectAllCheckboxRef.current.indeterminate = isSomeSelected
    }
  }, [selectedChatIds, filteredChats.length])

  const handleSendMessage = async (
    plainText: string,
    htmlContent: string,
  ): Promise<void> => {
    if (!selectedChat) return
    if (editingMessageId) {
      const updatedMessage = {
        content: plainText,
        html_content: htmlContent,
        is_edited: true,
      }

      try {
        await dispatch(
          updateInternalChat({
            chat_message_id: editingMessageId,
            data: updatedMessage,
          }),
        ).unwrap()
        setEditingMessageId(null)
        showToast(
          createSuccessToast(
            'Message saved',
            'Your message was edited successfully.',
          ),
        )
        setEditingMessageId(null)
        setEditorInput({
          text: '',
          htmlContent: '',
        })
      } catch {
        showToast(
          createFailedToast(
            'Edit failed',
            'Failed to edit message. Please try again.',
          ),
        )
      }
      return
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const participants = selectedChat.chat_participant.filter(
      (p) => p.user_id !== user.user_id,
    )
    const timestamp = new Date().toISOString()

    try {
      await dispatch(
        addInternalChatMessage({
          chat_conversation_id: selectedChat.chat_conversation_id!,
          content: plainText,
          html_content: htmlContent,
          sender_id: user.user_id,
          receiver_id: participants[0]?.user_id ?? '',
          timestamp,
          channel: ChatChannel.inApp,
          chat_direction: ChatDirection.outgoing,
          send_status: SendStatus.queued,
        }),
      ).unwrap()
      setEditorInput({
        text: '',
        htmlContent: '',
      })
    } catch {
      console.error('Failed to send message')
    }
  }

  const handleEditMessage = (
    messageId: string,
    messageText: string,
    htmlContent: string,
  ): void => {
    setEditingMessageId(messageId)
    setEditorInput({
      text: messageText,
      htmlContent: htmlContent,
    })
    toolbarRef.current?.focus()
  }

  const handleCreateConversation = async (
    user: IFavoritedUser,
  ): Promise<void> => {
    const chat = conversations?.find((c) => {
      if (
        c.is_hidden ||
        !c.chat_participant ||
        c.chat_participant.length !== 2
      ) {
        return false
      }

      return c.chat_participant.some((cp) => cp.user_id === user.user_id)
    })

    if (chat && chat.chat_conversation_id) {
      setSelectedChat(chat)
      return
    } else {
      const result = await dispatch(
        createInternalConversation([
          {
            chat_conversation_id: '',
            user_id: user.user_id,
            username: user.username,
          },
        ]),
      ).unwrap()
      const newConversation = result.conversation
        ? {
            ...result.conversation,
            chat_participant:
              result.participants ?? result.conversation.chat_participant ?? [],
            chat_message: [],
          }
        : null
      if (newConversation) {
        setSelectedChat(newConversation)
      }
    }
  }

  const handleNewChat = (): void => {
    setShowStartChatModal(true)
  }

  const handleStartChat = async (members: string[]): Promise<void> => {
    if (members.length === 0) return
    if (members.length === 1) {
      const conversation = conversations.find((c) => {
        if (c.is_hidden) return false
        const isPersonal =
          c.conversation_kind === 'personal' ||
          (c.chat_participant?.length === 2 && !c.conversation_kind)
        if (!isPersonal) return false
        return c.chat_participant?.some((p) => p.user_id === members[0])
      })
      if (conversation) {
        setSelectedChat(conversation)
        return
      }
      const user = users.find((u) => u.user_id === members[0])
      const singleParticipant: IChatParticipant[] = [
        {
          user_id: user?.user_id ?? members[0],
          username: user?.username,
          chat_conversation_id: '',
          chat_participant_id: undefined,
        },
      ]
      try {
        const result = await dispatch(
          createInternalConversation(singleParticipant),
        ).unwrap()
        const newConversation = result.conversation
        if (newConversation?.chat_conversation_id) {
          setSelectedChat(newConversation)
        }
        setShowStartChatModal(false)
      } catch {
        console.error('Failed to create new conversation.')
      }
      return
    }

    const allParticipantUserIds = new Set(members.filter(Boolean))

    const allParticipants: IChatParticipant[] = Array.from(
      allParticipantUserIds,
    ).map((userId) => {
      const user = users.find((u) => u.user_id === userId)
      return {
        user_id: user?.user_id ?? userId,
        username: user?.username,
        chat_conversation_id: '',
        chat_participant_id: undefined,
      }
    })
    const existingConversation = conversations.find((c) => {
      if (c.chat_participant?.length !== allParticipants.length) return false
      return c.chat_participant.every(
        (p) => p.user_id && allParticipantUserIds.has(p.user_id),
      )
    })

    if (existingConversation) {
      setSelectedChat(existingConversation)
      setSelectedChatIds([])
      setShowArchivedList(false)
      return
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    try {
      const result = await dispatch(
        createInternalConversation(
          allParticipants.filter((p) => p.user_id !== user.user_id),
        ),
      ).unwrap()
      const newConversation = result.conversation
        ? {
            ...result.conversation,
            chat_participant:
              result.participants ?? result.conversation.chat_participant ?? [],
          }
        : null
      if (newConversation) {
        setSelectedChat(newConversation)
      }
    } finally {
      setSelectedChatIds([])
      setShowArchivedList(false)
    }
    setShowStartChatModal(false)
  }

  const handleInsertLink = (url: string, linkText: string): void => {
    const cursorPosition =
      inputRef.current?.selectionStart || messageValue.length
    const formattedUrl =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`
    const linkMarkdown = `[${linkText}](${formattedUrl})`
    const newValue =
      messageValue.slice(0, cursorPosition) +
      linkMarkdown +
      messageValue.slice(cursorPosition)
    setMessageValue(newValue)
    setTimeout(() => {
      inputRef.current?.focus()
      const newCursorPos = cursorPosition + linkMarkdown.length
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const quickReplies = ['Okay', 'Sure thing', "I can't join"]

  const handleChatClick = (convo: IChatConversation): void => {
    setSelectedChat(convo)
    if (convo.chat_conversation_id) {
      dispatch(markInternalConversationAsRead(convo.chat_conversation_id))
    }
  }

  const requestRemoveMember = (member: IChatParticipant): void => {
    setSelectedRecipient(member)
    setShowConfirmRemoveMemberModal(true)
  }

  const handleRemoveMember = (): void => {
    const participantId = selectedRecipient?.chat_participant_id
    if (participantId) {
      dispatch(deleteChatParticipant(participantId))
      setShowMembersList(false)
    }
    setShowConfirmRemoveMemberModal(false)
    setSelectedRecipient(null)
  }

  const handleLeaveGroupClick = (): void => {
    setShowConfirmLeaveGroupModal(true)
    setShowMemberMenu(false)
  }

  const handleConfirmLeaveGroup = async (): Promise<void> => {
    const ownParticipantId = selectedChat?.chat_participant?.find(
      (p) =>
        p.user_id === JSON.parse(localStorage.getItem('user') || '{}').user_id,
    )?.chat_participant_id

    if (!ownParticipantId) return
    try {
      showLoading({ container: INTERNAL_CHAT_LOADING_CONTAINER })
      await dispatch(deleteChatParticipant(ownParticipantId)).unwrap()
      showToast(
        createSuccessToast('Chat left', 'You have left the chat successfully.'),
      )
    } catch {
      showToast(
        createFailedToast(
          'Chat left',
          'Failed to leave the chat. Please try again.',
        ),
      )
    } finally {
      hideLoading(INTERNAL_CHAT_LOADING_CONTAINER)
    }

    setSelectedChat(null)
    setShowConfirmLeaveGroupModal(false)
  }

  const handleChatCheckboxChange = (
    chatId: string,
    checked: boolean,
    e?: React.MouseEvent | React.ChangeEvent,
  ): void => {
    if (e) {
      e.stopPropagation()
    }
    setSelectedChatIds((prev) => {
      if (checked) {
        return [...prev, chatId]
      } else {
        return prev.filter((id) => id !== chatId)
      }
    })
  }

  const handleSelectAll = (): void => {
    if (selectedChatIds.length === filteredChats.length) {
      setSelectedChatIds([])
    } else {
      setSelectedChatIds(
        filteredChats.map((chat) => chat.chat_conversation_id ?? ''),
      )
    }
  }

  const handleArchiveChats = async (id?: string): Promise<void> => {
    const ids = id ? [id] : (selectedChatIds ?? [])
    if (ids.length === 0) return
    const isArchiving = !showArchivedList
    try {
      showLoading({ container: INTERNAL_CHAT_LOADING_CONTAINER })
      await dispatch(
        bulkUpdateConversation({
          ids,
          data: { is_archived: isArchiving },
        }),
      ).unwrap()
      showToast(
        createSuccessToast(
          isArchiving ? 'Archived' : 'Unarchived',
          isArchiving
            ? `${ids.length} conversation${ids.length === 1 ? '' : 's'} archived.`
            : `${ids.length} conversation${ids.length === 1 ? '' : 's'} unarchived.`,
        ),
      )
      setSelectedChatIds([])
    } catch {
      showToast(
        createFailedToast(
          isArchiving ? 'Archive failed' : 'Unarchive failed',
          `Failed to ${isArchiving ? 'archive' : 'unarchive'} conversation${ids.length === 1 ? '' : 's'}. Please try again.`,
        ),
      )
    } finally {
      hideLoading(INTERNAL_CHAT_LOADING_CONTAINER)
    }
  }

  const areSelectedChatsPinned = (): boolean => {
    const selectedChats = conversations.filter((c) =>
      selectedChatIds.includes(c.chat_conversation_id ?? ''),
    )
    if (selectedChats.length === 0) return false
    return selectedChats.every((c) => c.is_pinned)
  }

  const handlePinChats = async (): Promise<void> => {
    const ids = selectedChatIds ?? []
    if (ids.length === 0) return
    const isPinning = !areSelectedChatsPinned()
    try {
      showLoading({ container: INTERNAL_CHAT_LOADING_CONTAINER })
      await dispatch(
        bulkUpdateConversation({
          ids,
          data: { is_pinned: isPinning },
        }),
      ).unwrap()
      showToast(
        createSuccessToast(
          isPinning ? 'Pinned' : 'Unpinned',
          isPinning
            ? `${ids.length} conversation${ids.length === 1 ? '' : 's'} pinned.`
            : `${ids.length} conversation${ids.length === 1 ? '' : 's'} unpinned.`,
        ),
      )
      setSelectedChatIds([])
    } catch {
      showToast(
        createFailedToast(
          isPinning ? 'Pin failed' : 'Unpin failed',
          `Failed to ${isPinning ? 'pin' : 'unpin'} conversation${ids.length === 1 ? '' : 's'}. Please try again.`,
        ),
      )
    } finally {
      hideLoading(INTERNAL_CHAT_LOADING_CONTAINER)
    }
  }

  const handleCreateConversationFromSelection = async (): Promise<void> => {
    const selectedIds = Array.from(selectedChatIds)
    if (selectedIds.length === 0) return
    if (selectedIds.length === 1) {
      const conversation = conversations.find(
        (c) => c.chat_conversation_id === selectedIds[0],
      )
      if (conversation) {
        setSelectedChat(conversation)
      }
      return
    }

    const selectedConversations = conversations.filter((c) =>
      selectedIds.includes(c.chat_conversation_id ?? ''),
    )
    const allParticipants = selectedConversations
      .flatMap((c) => c.chat_participant)
      .filter(
        (participant, index, self) =>
          index === self.findIndex((p) => p.user_id === participant.user_id),
      )

    const allParticipantUserIds = new Set(
      allParticipants.map((p) => p.user_id).filter(Boolean),
    )
    const existingConversation = conversations.find((c) => {
      if (c.chat_participant?.length !== allParticipants.length) return false
      return c.chat_participant.every(
        (p) => p.user_id && allParticipantUserIds.has(p.user_id),
      )
    })

    if (existingConversation) {
      setSelectedChat(existingConversation)
      setSelectedChatIds([])
      setShowArchivedList(false)
      return
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    try {
      const result = await dispatch(
        createInternalConversation(
          allParticipants.filter((p) => p.user_id !== user.user_id),
        ),
      ).unwrap()
      const newConversation = result.conversation
        ? {
            ...result.conversation,
            chat_participant:
              result.participants ?? result.conversation.chat_participant ?? [],
          }
        : null
      if (newConversation) {
        setSelectedChat(newConversation)
      }
    } finally {
      setSelectedChatIds([])
      setShowArchivedList(false)
    }
  }

  const handleDeleteChats = async (): Promise<void> => {
    const selfParticipantIds: string[] = conversations
      .filter(
        (c) =>
          selectedChatIds.includes(c.chat_conversation_id ?? '') &&
          c.chat_participant?.some((p) => p.user_id === currentUserId),
      )
      .map((c) => {
        const selfParticipant = c.chat_participant?.find(
          (p) => p.user_id === currentUserId,
        )
        return selfParticipant?.chat_participant_id
      })
      .filter((id): id is string => id != null)

    if (selfParticipantIds.length === 0) return

    try {
      showLoading({ container: INTERNAL_CHAT_LOADING_CONTAINER })
      await dispatch(
        bulkUpdateChatParticipantsAction({
          ids: selfParticipantIds,
          data: { last_deleted_at: new Date().toISOString(), is_active: false },
        }),
      ).unwrap()
      showToast(
        createSuccessToast(
          selfParticipantIds.length === 1 ? 'Chat deleted' : 'Chats deleted',
          selfParticipantIds.length === 1
            ? 'The chat has been deleted.'
            : `${selfParticipantIds.length} chats have been deleted.`,
        ),
      )
      setSelectedChatIds([])
    } catch {
      showToast(
        createFailedToast(
          'Delete failed',
          'Failed to delete chats. Please try again.',
        ),
      )
    } finally {
      hideLoading(INTERNAL_CHAT_LOADING_CONTAINER)
    }
  }

  const getDeleteEntityName = (): string => {
    if (selectedChatIds.length === 1) {
      const id = Array.from(selectedChatIds)[0]
      const chat = conversations.find((c) => c.chat_conversation_id === id)
      return chat?.name || (chat && getConversationName(chat).length)
        ? `chat with ${getConversationName(chat)}`
        : 'this chat'
    }
    return `${selectedChatIds.length} chats`
  }

  const handleRefreshChats = async (): Promise<void> => {
    await dispatch(fetchAllInternalChats()).unwrap()
  }

  const handleCloseSelection = (): void => {
    setSelectedChatIds([])
  }

  useEffect(() => {
    if (conversations.length > 0) {
      const chat = conversations.find(
        (c) => c.chat_conversation_id === selectedChat?.chat_conversation_id,
      )
      setSelectedChat(chat ?? null)
    }
  }, [conversations])

  useEffect(() => {
    if (selectedChatId) {
      const chat = conversations.find(
        (c) => c.chat_conversation_id === selectedChatId,
      )
      if (chat) {
        setSelectedChat(chat)
        dispatch(setSelectedChatId(null))
      }
    }
  }, [selectedChatId])

  return (
    <div
      className={`h-screen flex flex-col bg-white internal-chat-sidepanel ${isHidden ? '' : 'open'}`}
      style={{
        width: isHidden ? '0' : '311px',
        border: isHidden ? 'none' : '1px solid var(--blue-200)',
        display: isHidden ? 'none' : '',
      }}
    >
      <div className="internal-chat-head">
        <div className="internal-chat-head-left">
          <div className="internal-chat-title">
            <span className="internal-chat-title-text">
              {selectedChat
                ? getConversationName(selectedChat)
                : 'Internal chat'}
            </span>
            {!selectedChat && (
              <button
                type="button"
                className="unstyled-button mgc_refresh_1_line"
                aria-label="Refresh chats"
                title="Refresh chats"
                onClick={handleRefreshChats}
              />
            )}
          </div>
          <button
            type="button"
            className="unstyled-button collapse-icon ti ti-layout-sidebar-right-collapse"
            aria-label="Collapse internal chat panel"
            onClick={toggle}
          />
        </div>
        {selectedChat && (
          <div className="internal-chat-back-container">
            <button
              type="button"
              className="unstyled-button internal-chat-back"
              onClick={() => {
                if (selectedChat.chat_conversation_id) {
                  dispatch(
                    markInternalConversationAsRead(
                      selectedChat.chat_conversation_id,
                    ),
                  )
                }
                setSelectedChat(null)
                setSelectedChat(null)
              }}
            >
              <i className="mgc_left_line" aria-hidden></i>
              <span className="internal-chat-back-text">Chats</span>
            </button>
            {selectedChat && (
              <div className="internal-chat-header-actions">
                <Dropdown
                  show={showMemberMenu}
                  onToggle={(isOpen) => setShowMemberMenu(isOpen)}
                >
                  <Dropdown.Toggle
                    variant="link"
                    id="chat-members-dropdown"
                    className="internal-chat-members-menu"
                    aria-label="Chat actions"
                  >
                    <i className="ti ti-dots-vertical" aria-hidden="true" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="internal-chat-members-dropdown-menu">
                    {selectedChat?.is_archived && (
                      <Dropdown.Item
                        onClick={() => {
                          handleArchiveChats(
                            selectedChat?.chat_conversation_id ?? '',
                          )
                          setShowMemberMenu(false)
                        }}
                        className="internal-chat-menu-item"
                      >
                        <i className="mgc_archive_line me-2"></i>
                        Unarchive chat
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      onClick={() => {
                        setShowMembersList(true)
                        setShowMemberMenu(false)
                      }}
                      className="internal-chat-menu-item"
                    >
                      <i className="ti ti-users me-2"></i>
                      See members
                    </Dropdown.Item>
                    {selectedChat.chat_participant &&
                      (selectedChat.chat_participant.length > 2 ||
                        selectedChat.name) && (
                        <>
                          <Dropdown.Item
                            onClick={() => {
                              setShowChangeNameModal(true)
                              setShowMemberMenu(false)
                            }}
                            className="internal-chat-menu-item"
                          >
                            <i className="ti ti-pencil me-2"></i>
                            Change group chat name
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={handleLeaveGroupClick}
                            className="internal-chat-menu-item internal-chat-menu-item-danger"
                          >
                            <i className="ti ti-logout me-2"></i>
                            Leave group
                          </Dropdown.Item>
                        </>
                      )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="internal-chat-body"
        style={{ display: isHidden ? 'none' : '' }}
      >
        {!selectedChat ? (
          <>
            <div className="internal-chat-search">
              <div className="internal-chat-search-container">
                <i className="mgc_search_line"></i>
                <input
                  type="text"
                  className="internal-chat-search-input"
                  placeholder="Search chat"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {searchValue.trim().length > 0 && (
                  <button
                    type="button"
                    className="unstyled-button mgc_close_line clear-button"
                    aria-label="Clear search"
                    onClick={() => setSearchValue('')}
                  />
                )}
                <button
                  type="button"
                  className="unstyled-button mgc_message_2_fill"
                  aria-label="New chat"
                  onClick={handleNewChat}
                />
              </div>
            </div>

            {!selectedChat && !showArchivedList && (
              <div className="internal-chat-quick-chat">
                <div className="internal-chat-quick-chat-header">
                  <div className="internal-chat-quick-chat-title">
                    Favorites
                  </div>
                  <a
                    href="#"
                    className="internal-chat-quick-chat-edit"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowEditFavoritesModal(true)
                    }}
                  >
                    Edit
                  </a>
                </div>
                {favoriteUsers.length > 0 ? (
                  <div className="internal-chat-quick-chat-avatars">
                    {favoriteUsers.map((user) => (
                      <TooltipComponent
                        key={user.user_id}
                        content={`${user.provider_profile ? 'Dr. ' : ''}${getFullName(user)}`}
                        position="TopCenter"
                        tabIndex={0}
                      >
                        <div
                          className="internal-chat-quick-avatar"
                          onClick={() => handleCreateConversation(user)}
                        >
                          {getConvoPhoto([
                            {
                              user_id: user.user_id,
                              username: user.username,
                              photo: user.provider_profile?.photo ?? undefined,
                              chat_conversation_id: '',
                            },
                          ])}
                        </div>
                      </TooltipComponent>
                    ))}
                  </div>
                ) : (
                  <span className="ic-qc-no-favorites">
                    No favorites yet. Tap Edit to add.
                  </span>
                )}
              </div>
            )}

            {showArchivedList && (
              <div className="internal-chat-archived-only">
                <button
                  type="button"
                  className="unstyled-button internal-chat-archived-back"
                  aria-label="Back to active chats"
                  onClick={() => {
                    setShowArchivedList(false)
                    setSelectedChatIds([])
                  }}
                />
              </div>
            )}

            <div className="internal-chat-list">
              {(archivedConversations.length > 0 || showArchivedList) && (
                <div className="internal-chat-archived-section">
                  <button
                    type="button"
                    className="unstyled-button internal-chat-archived-header internal-chat-archived-toggle"
                    onClick={() => {
                      setShowArchivedList(!showArchivedList)
                      setSelectedChatIds([])
                    }}
                  >
                    {showArchivedList ? (
                      <span className="internal-chat-archived-title">
                        <i className="mgc_left_line" aria-hidden></i>
                        <span>Active chats</span>
                      </span>
                    ) : (
                      <>
                        <span className="internal-chat-archived-title">
                          <i className="mgc_archive_line" aria-hidden></i>
                          <span>Archived chat</span>
                        </span>
                        <span className="internal-chat-archived-count">
                          {archivedConversations.length}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
              <div className="internal-chat-list-title">
                {showArchivedList ? 'Archived chats' : 'Chats'}
              </div>

              {selectedChatIds.length > 0 && (
                <div className="internal-chat-selection-toolbar">
                  <div className="internal-chat-selection-info">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={selectedChatIds.length === filteredChats.length}
                      onChange={handleSelectAll}
                      className="internal-chat-selection-checkbox"
                    />
                    <span className="internal-chat-selection-count">
                      {selectedChatIds.length} chat
                      {selectedChatIds.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="internal-chat-selection-actions">
                    <TooltipComponent
                      content={showArchivedList ? 'Unarchive' : 'Archive'}
                      position="TopCenter"
                    >
                      <button
                        type="button"
                        className="internal-chat-selection-action"
                        aria-label={showArchivedList ? 'Unarchive' : 'Archive'}
                        onClick={() => handleArchiveChats()}
                      >
                        <i
                          className={
                            showArchivedList
                              ? 'mgc_unarchive_line'
                              : 'mgc_archive_line'
                          }
                          aria-hidden="true"
                        />
                      </button>
                    </TooltipComponent>
                    <TooltipComponent
                      content={areSelectedChatsPinned() ? 'Unpin' : 'Pin'}
                      position="TopCenter"
                    >
                      <button
                        type="button"
                        className="internal-chat-selection-action"
                        aria-label={areSelectedChatsPinned() ? 'Unpin' : 'Pin'}
                        aria-pressed={areSelectedChatsPinned()}
                        onClick={handlePinChats}
                      >
                        <i className="mgc_pin_fill" aria-hidden="true" />
                      </button>
                    </TooltipComponent>
                    <TooltipComponent
                      content="New conversation"
                      position="TopCenter"
                    >
                      <button
                        type="button"
                        className="internal-chat-selection-action"
                        aria-label="New conversation"
                        onClick={handleCreateConversationFromSelection}
                      >
                        <i className="mgc_message_4_fill" aria-hidden="true" />
                      </button>
                    </TooltipComponent>
                    <TooltipComponent content="Delete" position="TopCenter">
                      <button
                        type="button"
                        className="internal-chat-selection-action internal-chat-selection-action-delete"
                        aria-label="Delete"
                        onClick={() => {
                          if (selectedChatIds.length > 0) {
                            setShowConfirmDeleteModal(true)
                          }
                        }}
                      >
                        <i className="mgc_delete_2_fill" aria-hidden="true" />
                      </button>
                    </TooltipComponent>
                    <TooltipComponent content="Close" position="TopCenter">
                      <button
                        type="button"
                        className="internal-chat-selection-action"
                        aria-label="Close"
                        onClick={handleCloseSelection}
                      >
                        <i className="mgc_close_line" aria-hidden="true" />
                      </button>
                    </TooltipComponent>
                  </div>
                </div>
              )}
              <div className="internal-chat-items">
                {filteredChats.length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'var(--gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    No chats found
                    {`${searchValue.length > 0 ? ` matching "${searchValue}"` : '.'}`}
                  </div>
                ) : (
                  filteredChats.map((chat) => {
                    const unreadCount = getInternalChatUnreadCount(
                      chat,
                      currentUserId,
                    )
                    return (
                      <div
                        key={chat.chat_conversation_id}
                        className={`internal-chat-item ${
                          chat.chat_conversation_id &&
                          selectedChatIds.includes(chat.chat_conversation_id)
                            ? 'internal-chat-item-selected'
                            : ''
                        } ${unreadCount > 0 ? 'unread' : ''}`}
                        onClick={(e) => {
                          if (selectedChatIds.length > 0) {
                            handleChatCheckboxChange(
                              chat.chat_conversation_id ?? '',
                              !chat.chat_conversation_id ||
                                !selectedChatIds.includes(
                                  chat.chat_conversation_id,
                                ),
                              e,
                            )
                          } else {
                            handleChatClick(chat)
                          }
                        }}
                        onMouseEnter={() =>
                          setHoveredChatId(chat.chat_conversation_id ?? '')
                        }
                        onMouseLeave={() => setHoveredChatId(null)}
                      >
                        {(hoveredChatId === chat.chat_conversation_id ||
                          selectedChatIds.includes(
                            chat.chat_conversation_id ?? '',
                          )) && (
                          <div
                            className="internal-chat-item-checkbox"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleChatCheckboxChange(
                                chat.chat_conversation_id ?? '',
                                !selectedChatIds.includes(
                                  chat.chat_conversation_id ?? '',
                                ),
                                e,
                              )
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedChatIds.includes(
                                chat.chat_conversation_id ?? '',
                              )}
                              onChange={(e) => {
                                e.stopPropagation()
                                const isChecked = e.target.checked
                                setSelectedChatIds((prev) => {
                                  if (isChecked) {
                                    return [
                                      ...prev,
                                      chat.chat_conversation_id ?? '',
                                    ]
                                  } else {
                                    return prev.filter(
                                      (id) => id !== chat.chat_conversation_id,
                                    )
                                  }
                                })
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                        <div className="internal-chat-item-avatar">
                          {getConvoPhoto(chat.chat_participant, !!chat.name)}
                        </div>
                        <div className="internal-chat-item-content">
                          <div className="internal-chat-item-header">
                            <span className="internal-chat-item-name">
                              {highlightText(
                                getConversationName(chat),
                                searchValue,
                              )}
                            </span>
                            <span className="internal-chat-item-time">
                              {
                                generateTimeDisplay(
                                  chat.chat_message.length > 0
                                    ? new Date(
                                        chat.chat_message[
                                          chat.chat_message.length - 1
                                        ].timestamp,
                                      )
                                    : new Date(
                                        chat.date_time_created_at ?? new Date(),
                                      ),
                                )[0]
                              }
                            </span>
                          </div>
                          <div className="internal-chat-item-message">
                            <span className="last-chat">
                              {highlightText(
                                getFirstLineFromHTML(
                                  getDisplayChat(chat.chat_message),
                                ),
                                searchValue,
                              )}
                            </span>
                            {currentUserId && unreadCount > 0 && (
                              <span
                                className="internal-chat-item-unread-badge"
                                title="Unread messages"
                              >
                                {unreadCount}
                              </span>
                            )}
                            {chat.is_pinned && (
                              <i className="mgc_pin_fill internal-chat-item-pin-icon"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="internal-chat-exchange-container allow-user-select">
            {selectedChat?.chat_message?.map((msg, index) => {
              const messages = selectedChat?.chat_message ?? []
              const isLastMessage = index === messages.length - 1
              const sender = getSenderDetails(
                msg.sender_id,
                selectedChat?.chat_participant ?? [],
                true,
              )
              return (
                <div
                  key={msg.chat_conversation_id}
                  className={`internal-chat-exchange-message ${getChatDirection(msg) === 'Outgoing' ? 'internal-chat-exchange-message-own' : ''}`}
                >
                  <div className="internal-chat-exchange-avatar">
                    {getConvoPhoto([
                      {
                        user_id: sender?.user_id ?? '',
                        username: sender?.username ?? '',
                        photo: sender?.photo ?? '',
                        chat_conversation_id: '',
                      },
                    ])}
                  </div>
                  <div className="internal-chat-exchange-content">
                    <div className="internal-chat-exchange-header">
                      <span
                        className="internal-chat-exchange-name"
                        style={
                          sender?.user?.provider_profile?.provider_color
                            ? {
                                color:
                                  typeof sender.user.provider_profile
                                    .provider_color === 'string'
                                    ? sender.user.provider_profile
                                        .provider_color
                                    : sender.user.provider_profile
                                        .provider_color.header,
                              }
                            : undefined
                        }
                      >
                        {getParticipantDisplayName(sender)}
                      </span>
                      <span className="internal-chat-exchange-time">
                        {isLastMessage &&
                        trackMessageStatus &&
                        getParticipantDisplayName(sender) === 'You' &&
                        msg.chat_direction === ChatDirection.outgoing &&
                        msg.send_status === SendStatus.queued
                          ? 'Sending...'
                          : generateTimeDisplay(new Date(msg.timestamp))[1]}
                      </span>
                    </div>
                    <div className="internal-chat-exchange-message-text">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeRichTextHtml(msg.html_content),
                        }}
                      />
                    </div>
                    {(msg.chat_message_id === editingMessageId ||
                      msg.is_edited) && (
                      <span className="edit-label">
                        {msg.chat_message_id === editingMessageId
                          ? 'Editing'
                          : 'Edited'}
                      </span>
                    )}
                  </div>
                  {getChatDirection(msg) === 'Outgoing' && (
                    <button
                      type="button"
                      className="unstyled-button internal-chat-edit-icon"
                      onClick={() => {
                        handleEditMessage(
                          msg.chat_message_id ?? '',
                          msg.content ?? '',
                          msg.html_content ?? '',
                        )
                      }}
                      aria-label="Edit message"
                      title="Edit message"
                    >
                      <i className="mgc_pencil_fill" aria-hidden></i>
                    </button>
                  )}
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {selectedChat && (
        <div
          className="internal-chat-footer"
          style={{ display: isHidden ? 'none' : '' }}
        >
          {selectedChat && (
            <>
              <div className="internal-chat-footer-title">Quick replies</div>
              <div className="internal-chat-footer-badges">
                {quickReplies.map((reply, index) => (
                  <span
                    key={index}
                    className="internal-chat-footer-badge"
                    onClick={() => {
                      handleSendMessage(reply, reply)
                    }}
                  >
                    {reply}
                  </span>
                ))}
              </div>
            </>
          )}
          <SPTextEditor
            editorInput={editorInput}
            isEditing={editingMessageId !== null}
            onSend={handleSendMessage}
            setEditorInput={setEditorInput}
            toolbarRef={toolbarRef}
          />
        </div>
      )}

      <StartChatModal
        isOpen={showStartChatModal}
        onRequestClose={() => setShowStartChatModal(false)}
        onStartChat={handleStartChat}
        availableMembers={users}
      />

      <EditFavoritesModal
        isOpen={showEditFavoritesModal}
        onRequestClose={() => setShowEditFavoritesModal(false)}
        availableMembers={users}
        currentFavorites={favoriteUsers}
      />

      {selectedChat?.chat_participant && (
        <InternalChatMembersModal
          show={showMembersList}
          onHide={() => setShowMembersList(false)}
          recipient={null}
          members={selectedChat?.chat_participant ?? []}
          onRemoveMember={requestRemoveMember}
        />
      )}

      {selectedChat && (
        <InternalChatChangeNameModal
          show={showChangeNameModal}
          onHide={() => setShowChangeNameModal(false)}
          initialValue={selectedChat.name ?? ''}
          conversationId={selectedChat.chat_conversation_id ?? ''}
        />
      )}

      <ConfirmDeleteModal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        onConfirm={() => {
          handleDeleteChats()
          setShowConfirmDeleteModal(false)
        }}
        entityName={getDeleteEntityName()}
      />

      {selectedRecipient && (
        <ConfirmDeleteModal
          show={showConfirmRemoveMemberModal}
          onHide={() => {
            setShowConfirmRemoveMemberModal(false)
            setSelectedRecipient(null)
          }}
          onConfirm={handleRemoveMember}
          entityName={selectedRecipient.username || 'this member'}
          title="Confirm removal?"
          message="Are you sure you want to remove"
          confirmButtonText="Remove"
        />
      )}

      <InsertLinkModal
        isOpen={showInsertLinkModal}
        onRequestClose={() => setShowInsertLinkModal(false)}
        onInsertLink={handleInsertLink}
      />

      <ConfirmDeleteModal
        show={showConfirmLeaveGroupModal}
        onHide={() => setShowConfirmLeaveGroupModal(false)}
        onConfirm={handleConfirmLeaveGroup}
        entityName="this group"
        title="Leave Group Chat?"
        message="Are you sure you want to leave"
        confirmButtonText="Leave"
      />
    </div>
  )
}
export default InternalChatSidePanel
